/** The four verdicts a check can carry — `CheckLite['status']`, restated here so
 *  this module stays free of the worker protocol. */
export type CheckStatus = 'pass' | 'info' | 'warn' | 'fail';

/** How loud the summary line is: the colour of its dot, nothing more. */
export type SummaryTone = 'ok' | 'warn' | 'fail';

/**
 * The inspector's whole list said in one line — what the shop reads instead of
 * scrolling 574–860 px of rows (UX audit #1).
 *
 * The words are deliberately not the pill words: a `warn` is "a thing to
 * check" and a `fail` is "a problem", because "2 warn" tells a first-time user
 * nothing about whether they may print. `info` rows are measurements, not
 * verdicts, so they count as clear — and failures are never folded into the
 * warning count: with both, both are said, failures first.
 *
 * `errors` is how many errors the panel's pinned footer is showing — a failed
 * plate build, a mockup that could not render. They are not checks and the
 * words never claim to count them, but they decide the tone: a green dot above
 * a red status line is the panel disagreeing with itself, and the status line
 * is the one that is right.
 */
export function inspectorSummary(
  checks: ReadonlyArray<{ status: CheckStatus }>,
  errors = 0,
): { text: string; tone: SummaryTone } {
  const tone = (t: SummaryTone): SummaryTone => (errors > 0 ? 'fail' : t);
  if (!checks.length) return { text: 'No checks yet', tone: tone('ok') };
  const fails = checks.filter((c) => c.status === 'fail').length;
  const warns = checks.filter((c) => c.status === 'warn').length;
  if (!fails && !warns) return { text: 'All checks pass', tone: tone('ok') };
  const things = `${warns} thing${warns === 1 ? '' : 's'} to check`;
  if (!fails) return { text: things, tone: tone('warn') };
  const problems = `${fails} problem${fails === 1 ? '' : 's'}`;
  return { text: warns ? `${problems}, ${things}` : problems, tone: 'fail' };
}

/**
 * What the viewer is actually painting — not the app mode. In screen-print mode
 * with a failed or not-yet-built separation the canvas is still the DTF output,
 * and in mockup mode without a render it is too.
 */
export type CanvasPainting = 'output' | 'separation' | 'mockup';

/**
 * Whether a check row is a control or a statement (UX audit #23: rows that do
 * something must look like it, and the ones that do nothing must not).
 *
 * A row is a button only when it has an overlay AND the canvas is showing the
 * output that overlay is a mask over. Over a plate or a garment photo, toggling
 * one would light the row up and paint nothing — and an overlay left on before
 * the switch could never be turned off again.
 */
export function rowKind(check: { hasOverlay?: boolean }, painting: CanvasPainting): 'button' | 'text' {
  return check.hasOverlay && painting === 'output' ? 'button' : 'text';
}
